import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import styles from "./Select.module.css";
import MultiSelect from "./MultiSelect.jsx";
import NativeSelect from "./NativeSelect.jsx";
import SelectTrigger from "./SelectTrigger.jsx";
import SelectMenu from "./SelectMenu.jsx";
import { supportsBaseSelect } from "../../utils/browser.js";
import { getClassName } from "../../utils/className.js";
import { normalizeOptions } from "./utils.js";

const getFixedOrigin = (container) => {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none";
  container.appendChild(probe);
  const { top, left } = probe.getBoundingClientRect();
  container.removeChild(probe);
  return { top, left };
};

/**
 * @typedef {Object} SelectOption
 * @property {string | number} value - The unique value of the option.
 * @property {string} label - The display text of the option.
 * @property {boolean} [disabled] - Whether the option is disabled.
 * @property {React.ReactNode} [icon] - Optional icon/element rendered before the label in the dropdown list (e.g. a flag).
 */

/**
 * Select is a premium, highly-accessible custom selection component.
 * Supports native dropdown rendering, search filtering, async loading options, and multiple tags/chips.
 *
 * @component
 * @param {Object} props
 * @param {Array<SelectOption | string | number>} props.options - Array of option objects or primitives.
 * @param {string | number | Array<string | number>} [props.value] - Selected option value(s).
 * @param {(value: any, option?: any) => void} [props.onChange] - Callback fired when option is selected.
 * @param {string} [props.placeholder] - Custom placeholder text (defaults to "Select...").
 * @param {string} [props.label] - Optional header label text above the field.
 * @param {boolean} [props.disabled] - Whether the select component is disabled.
 * @param {boolean | string} [props.error] - If boolean/string, styles field as invalid. If string, displays it as helper error text.
 * @param {string} [props.helperText] - Optional helper/hint text displayed below the field.
 * @param {boolean} [props.fullWidth] - If true, takes 100% width of parent container (defaults to false).
 * @param {boolean} [props.searchable] - If true, enables inline search inside the trigger value box.
 * @param {boolean} [props.menuSearch] - If true, renders a search input at the top of the dropdown menu instead of the trigger.
 * @param {React.ComponentType | React.ReactElement} [props.arrowIcon] - Custom icon component or element to replace the default dropdown arrow.
 * @param {(query: string) => Promise<Array<string | number | SelectOption>>} [props.asyncOptions] - A function that receives the search filter value and returns a promise resolving to options.
 * @param {number} [props.asyncDebounceMs] - Debounce delay in milliseconds for async option fetching (defaults to 250).
 * @param {boolean} [props.menuPortal] - If true, the dropdown menu is rendered in a portal with fixed positioning. Defaults to false.
 * @param {string | number} [props.limitWidth] - The max-width of the menu in menu portal mode. Defaults to "450px".
 * @param {string | number} [props.maxMenuWidth] - Limits the max-width of the dropdown menu in both portal and non-portal modes.
 * @param {string | number} [props.maxMenuHeight] - Caps the dropdown menu's height so long option lists scroll instead of running down the page. Number is treated as px. Overrides the viewport-derived default of `calc(100vh - 120px)`.
 * @param {string | number} [props.minMenuWidth] - Minimum width of the dropdown menu. Number is treated as px. In portal mode the menu still never renders narrower than its trigger.
 * @param {string} [props.className] - External class name to style the container.
 * @param {Object} [props.classes] - Custom classes for inner components.
 * @param {string} [props.classes.root] - Custom class for root container.
 * @param {string} [props.classes.label] - Custom class for label text.
 * @param {string} [props.classes.trigger] - Custom class for trigger button/input.
 * @param {string} [props.classes.menu] - Custom class for menu list container.
 * @param {string} [props.classes.option] - Custom class for option list items.
 * @param {React.CSSProperties} [props.style] - Inline style for container element.
 * @param {string} [props.name] - HTML name attribute for form support.
 * @param {string} [props.id] - Unique ID for the wrapper element.
 * @param {boolean} [props.multiple] - Enables multi-select mode.
 * @param {boolean} [props.multi] - Alias for multiple.
 * @param {boolean} [props.isMulti] - Alias for multiple.
 * @param {() => void} [props.onLoadMore] - Callback triggered when the options menu is scrolled to bottom.
 * @param {boolean} [props.hasMore] - If true, triggers onLoadMore on scroll and displays a spinner.
 * @param {boolean} [props.isLoadingMore] - If true, displays loading indicator at list bottom.
 * @param {boolean} [props.native] - If true, renders a native select element (always opens dropdown).
 * @param {boolean} [props.simulateNativeOnChange] - If true, calls onChange with a simulated native event { target: { name, value } }.
 * @param {boolean} [props.adaptiveWidth] - If true, the select width adapts to the selected option's content size (single-select only), treating the JSS/CSS defined width as a minimum width and capping at 100% max-width.
 * @param {boolean} [props.fitOptionsWidth] - If true, the select trigger grows to fit the longest option label so no text is truncated with an ellipsis.
 * @param {boolean} [props.floatingLabel] - If true, renders the label as a floating label that animates above the trigger when focused or a value is selected.
 * @param {number | string} [props.height] - Override the trigger height (e.g. 56 or "56px").
 * @param {boolean} [props.preventBlurOnSelect] - If true, prevents the input from blurring when an option is selected.
 * @param {string} [props.tooltip] - Tooltip text shown (with a short hover delay) when hovering over the trigger.
 * @param {boolean} [props.tooltipLight] - If true, (white background, dark text).
 * @param {'small' | 'medium' | 'large'} [props.size] - Controls the trigger's height and font size, the same three-tier scale as `Button`. Omit for the unsized default (40px) — note `size="medium"` (34px) is smaller than that default, matching `Button`'s own convention. `height` is an inline style and wins over `size` if both are set.
 *
 * @example
 * // With adaptiveWidth enabled:
 * <Select
 *   adaptiveWidth
 *   options={[{ value: "limited", label: "Limited Option" }, { value: "service-provider", label: "Service Provider" }]}
 * />
 *
 * // With simulateNativeOnChange enabled for form library integrations:
 * <Select
 *   name="status"
 *   simulateNativeOnChange
 *   onChange={(e) => console.log(e.target.value)}
 *   options={[{ value: "active", label: "Active" }]}
 * />
 */
const Select = ({
  options = [],
  value,
  onChange,
  placeholder: placeholderProp = "",
  label: labelProp,
  disabled = false,
  error = false,
  helperText,
  fullWidth = false,
  searchable = false,
  menuSearch = false,
  asyncOptions,
  asyncDebounceMs = 250,
  menuPortal = false,
  limitWidth = "450px",
  maxMenuWidth,
  maxMenuHeight,
  minMenuWidth,
  className: externalClassName,
  classes = {},
  style,
  name,
  id,
  multiple,
  multi,
  isMulti,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  native = false,
  simulateNativeOnChange = false,
  adaptiveWidth = false,
  fitOptionsWidth = false,
  arrowIcon,
  floatingLabel = false,
  height,
  preventBlurOnSelect = false,
  tooltip,
  tooltipLight = false,
  size,
  ...rest
}) => {
  const sizeClass = size === "small" ? styles.small : size === "medium" ? styles.medium : size === "large" ? styles.large : null;
  let label = labelProp;
  let placeholder = placeholderProp;

  if (floatingLabel) {
    if (!label) {
      if (placeholder) {
        label = placeholder;
        placeholder = "";
      } else {
        floatingLabel = false;
      }
    }
  }
  const isMultiMode = multiple || multi || isMulti;

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchValue, setSearchValue] = useState("");
  const [asyncResults, setAsyncResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAsyncOption, setSelectedAsyncOption] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isBaseSelectSupported, setIsBaseSelectSupported] = useState(false);
  // Ref, not state — must be readable synchronously inside updateMenuPos.
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const renderedCountRef = useRef(0);

  useEffect(() => {
    setIsBaseSelectSupported(supportsBaseSelect());
  }, []);

  const containerRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Normalize static options to standard objects
  const normalizedOptions = useMemo(() => {
    return normalizeOptions(options);
  }, [options]);

  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  const [measuredMinWidth, setMeasuredMinWidth] = useState(undefined);
  const [fitWidth, setFitWidth] = useState(undefined);

  useIsomorphicLayoutEffect(() => {
    if (!adaptiveWidth || !containerRef.current) return;

    const el = containerRef.current;
    const originalWidthStyle = el.style.width;
    el.style.width = ""; // temporarily clear inline width to measure style sheet/JSS width

    const computed = window.getComputedStyle(el);
    const cssWidth = computed.width;

    el.style.width = originalWidthStyle;

    if (cssWidth && cssWidth !== "auto" && cssWidth !== "0px") {
      setMeasuredMinWidth(cssWidth);
    }
  }, [adaptiveWidth, externalClassName, classes.root, style]);

  // Measure the widest option label and set a min-width so no text is cut off
  useIsomorphicLayoutEffect(() => {
    if (!fitOptionsWidth || !normalizedOptions.length || typeof document === "undefined") return;

    const ruler = document.createElement("span");
    ruler.style.cssText = [
      "position:fixed",
      "visibility:hidden",
      "white-space:nowrap",
      "font-size:0.875rem",
      // mirror the trigger's font-family via CSS variable, fall back to Roboto
      "font-family:var(--ca-theme-typography-fontFamily,Roboto,sans-serif)",
      "pointer-events:none",
      "padding:0",
      "border:0",
      "margin:0",
    ].join(";");
    document.body.appendChild(ruler);

    let maxW = 0;
    const candidates = [
      ...normalizedOptions.map((o) => o.label),
      placeholder,
    ].filter(Boolean);

    for (const text of candidates) {
      ruler.textContent = text;
      maxW = Math.max(maxW, ruler.offsetWidth);
    }

    document.body.removeChild(ruler);

    // Add: 12px left padding + 32px right (arrow) + 2px border × 2 + 4px breathing room
    setFitWidth(maxW + 12 + 32 + 4 + 4);
  }, [fitOptionsWidth, normalizedOptions, placeholder]);

  const containerStyle = useMemo(() => {
    if (fitOptionsWidth && fitWidth != null && !isMultiMode) {
      return {
        ...style,
        minWidth: fitWidth,
        width: fitWidth,
      };
    }
    if (adaptiveWidth && !isMultiMode) {
      return {
        ...style,
        width: "max-content",
        maxWidth: "100%",
      };
    }
    return style;
  }, [style, adaptiveWidth, fitOptionsWidth, fitWidth, isMultiMode, measuredMinWidth]);

  const isSearchable = (searchable && !menuSearch) || !!asyncOptions;

  // Anchor dropdown menu to the trigger with fixed positioning when portaling.
  const updateMenuPos = useCallback(() => {
    const anchor = triggerRef.current || containerRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();

    let top = undefined;
    const bottom = "auto";
    let left = r.left;
    let width = r.width;
    let maxHeight = undefined;

    const menuHeight = menuRef.current ? menuRef.current.offsetHeight : 150;
    // With the keyboard open, iOS Safari draws position:fixed relative to the scrolled document.
    const origin = getFixedOrigin(anchor.closest("dialog") || document.body);
    // Visible area in client coords; visualViewport excludes the on-screen keyboard.
    const vv = window.visualViewport;
    const visibleTop = vv ? Math.max(0, vv.offsetTop + origin.top) : 0;
    const viewportHeight = visibleTop + (vv ? vv.height : window.innerHeight);

    if (!isSearchable) {
      const spaceBelow = viewportHeight - r.bottom - 10;
      if (spaceBelow >= menuHeight) {
        // Enough room below, position it directly below the select (without covering it)
        top = r.bottom;
      } else {
        // Default overlay mode (like MUI) - can and should cover/hide the select element
        top = r.top;
        if (floatingLabel) {
          top -= 8; // Offset to the top to hide the floating label
        }

        // Check boundaries and adjust to leave at least 10px of gap from viewport edges
        if (top < visibleTop + 10) {
          top = visibleTop + 10;
        }
        if (top + menuHeight > viewportHeight - 10) {
          top = Math.max(visibleTop + 10, viewportHeight - menuHeight - 10);
        }
      }
    } else {
      // Searchable mode - keep the trigger element (with the searchbox) visible
      const spaceBelow = viewportHeight - r.bottom - 10;
      const spaceAbove = r.top - visibleTop - 10;

      let preferBottom = true;
      if (spaceBelow >= menuHeight) {
        preferBottom = true;
      } else if (spaceAbove >= menuHeight) {
        preferBottom = false;
      } else if (spaceBelow >= spaceAbove) {
        preferBottom = true;
      } else {
        preferBottom = false;
      }

      if (preferBottom) {
        top = r.bottom;
        maxHeight = spaceBelow;
      } else {
        const offset = floatingLabel ? 4 : 0;
        const actualMenuHeight = Math.min(menuHeight, spaceAbove + offset);
        top = r.top + offset - actualMenuHeight;
        maxHeight = spaceAbove + offset;
      }
    }

    if (menuRef.current) {
      renderedCountRef.current += 1;
    }
    const enableTransition = renderedCountRef.current > 1 && !isScrollingRef.current;

    top -= origin.top;
    left -= origin.left;

    setMenuPos({ left, top, bottom, width, maxHeight, enableTransition });
  }, [isSearchable, floatingLabel]);

  const portalTarget =
    menuPortal && typeof document !== "undefined"
      ? (triggerRef.current && triggerRef.current.closest("dialog")) || document.body
      : null;

  // True once the trigger has scrolled fully outside a clipping ancestor.
  const isTriggerClippedFromView = () => {
    const el = triggerRef.current;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    let ancestor = el.parentElement;
    while (ancestor && ancestor !== document.body) {
      const cs = window.getComputedStyle(ancestor);
      const clipsX = cs.overflowX === "auto" || cs.overflowX === "scroll" || cs.overflowX === "hidden";
      const clipsY = cs.overflowY === "auto" || cs.overflowY === "scroll" || cs.overflowY === "hidden";
      if (clipsX || clipsY) {
        const ar = ancestor.getBoundingClientRect();
        const outsideX = clipsX && (rect.right <= ar.left || rect.left >= ar.right);
        const outsideY = clipsY && (rect.bottom <= ar.top || rect.top >= ar.bottom);
        if (outsideX || outsideY) return true;
      }
      ancestor = ancestor.parentElement;
    }
    return false;
  };

  useIsomorphicLayoutEffect(() => {
    if (!menuPortal || !isOpen) return undefined;

    updateMenuPos();
    const rafId = requestAnimationFrame(() => {
      updateMenuPos();
    });

    const handleScroll = () => {
      if (isTriggerClippedFromView()) {
        setIsOpen(false);
        return;
      }
      isScrollingRef.current = true;
      updateMenuPos();

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };

    const handleResize = () => updateMenuPos();
    const vv = window.visualViewport;

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    vv?.addEventListener("resize", handleScroll);
    vv?.addEventListener("scroll", handleScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      vv?.removeEventListener("resize", handleScroll);
      vv?.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [menuPortal, isOpen, searchValue, updateMenuPos]);



  // Fetch async options when dropdown opens or search query changes
  useEffect(() => {
    if (!isOpen || !asyncOptions) return;

    let active = true;
    setIsLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const results = await asyncOptions(searchValue);
        if (active) {
          const normalized = (results || []).map((option) => {
            if (option && typeof option === "object") {
              return {
                value: option.value,
                label: option.label !== undefined ? option.label : String(option.value),
                disabled: !!option.disabled,
              };
            }
            return {
              value: option,
              label: String(option),
              disabled: false,
            };
          });
          setAsyncResults(normalized);
          setIsLoading(false);
        }
      } catch (err) {
        if (active) {
          console.error("Error loading async select options:", err);
          setAsyncResults([]);
          setIsLoading(false);
        }
      }
    }, asyncDebounceMs);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [isOpen, searchValue, asyncOptions, asyncDebounceMs]);

  // Clean search states and results when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
      setAsyncResults([]);
      setIsLoading(false);
      renderedCountRef.current = 0;
    }
  }, [isOpen]);

  // Resolve options to display in list (handles async state, local search state, and defaults)
  const displayedOptions = useMemo(() => {
    if (asyncOptions) {
      return asyncResults;
    }
    if (!searchValue) {
      return normalizedOptions;
    }
    const query = searchValue.toLowerCase();
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(query)
    );
  }, [asyncOptions, asyncResults, normalizedOptions, searchValue]);

  // Sync selectedAsyncOption when value or normalizedOptions change
  useEffect(() => {
    if (value === undefined || value === null || value === "") {
      setSelectedAsyncOption(null);
    } else {
      const foundStatic = normalizedOptions.find((opt) => opt.value === value);
      if (foundStatic) {
        setSelectedAsyncOption(foundStatic);
      }
    }
  }, [value, normalizedOptions]);

  // Merge local and async options so that current selected item renders correctly
  const allKnownOptions = useMemo(() => {
    const map = new Map();
    normalizedOptions.forEach((opt) => map.set(opt.value, opt));
    asyncResults.forEach((opt) => map.set(opt.value, opt));
    if (selectedAsyncOption) {
      map.set(selectedAsyncOption.value, selectedAsyncOption);
    }
    return Array.from(map.values());
  }, [normalizedOptions, asyncResults, selectedAsyncOption]);

  // Find currently selected option
  const selectedOption = useMemo(() => {
    return allKnownOptions.find((opt) => opt.value == value) || null;
  }, [allKnownOptions, value]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current && containerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inContainer && !inMenu) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("touchstart", handleClickOutside, true);
    };
  }, [isOpen]);

  // Set keyboard active index
  useEffect(() => {
    if (isOpen) {
      const initialFocus = displayedOptions.findIndex((opt) => opt.value === value);
      setFocusedIndex(initialFocus >= 0 ? initialFocus : 0);
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, displayedOptions, value]);

  // Auto-scroll hovered option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && listRef.current) {
      const listEl = listRef.current;
      const optionEl = listEl.children[focusedIndex];
      if (optionEl) {
        const listRect = listEl.getBoundingClientRect();
        const optionRect = optionEl.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) {
          listEl.scrollTop += optionRect.bottom - listRect.bottom;
        } else if (optionRect.top < listRect.top) {
          listEl.scrollTop -= listRect.top - optionRect.top;
        }
      }
    }
  }, [focusedIndex, isOpen]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleArrowClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (disabled) return;
      setIsOpen((prev) => !prev);
      if (!isOpen && triggerRef.current) {
        triggerRef.current.focus();
      }
    },
    [disabled, isOpen]
  );

  const handleInputClick = useCallback(
    (e) => {
      if (disabled) return;
      if (!isOpen) {
        setIsOpen(true);
      }
    },
    [disabled, isOpen]
  );

  const handleInputFocus = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
  }, [disabled]);

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
    setIsOpen(true);
  }, []);

  const handleSelect = useCallback(
    (val) => {
      const option = allKnownOptions.find((opt) => opt.value === val);
      if (!option || option.disabled) return;
      setSelectedAsyncOption(option);
      if (simulateNativeOnChange) {
        onChange?.({
          target: {
            name,
            value: val,
          },
          preventDefault: () => { },
          stopPropagation: () => { },
        }, option);
      } else {
        onChange?.(val, option);
      }
      setIsOpen(false);
      if (triggerRef.current) {
        if (preventBlurOnSelect) {
          triggerRef.current.focus();
        } else {
          triggerRef.current.blur();
        }
      }
    },
    [allKnownOptions, onChange, simulateNativeOnChange, name, preventBlurOnSelect]
  );

  const handleClearSearch = useCallback((e) => {
    e.stopPropagation();
    setSearchValue("");
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;

      switch (e.key) {
        case "Enter":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            const opt = displayedOptions[focusedIndex];
            if (!opt.disabled) {
              handleSelect(opt.value);
            }
          }
          break;
        case " ":
          // Don't toggle selection if typing space in search trigger input
          if (e.target !== triggerRef.current) {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
              const opt = displayedOptions[focusedIndex];
              if (!opt.disabled) {
                handleSelect(opt.value);
              }
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          if (triggerRef.current && !preventBlurOnSelect) {
            triggerRef.current.blur();
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setFocusedIndex((prev) => {
              let nextIdx = prev + 1;
              while (nextIdx < displayedOptions.length && displayedOptions[nextIdx].disabled) {
                nextIdx++;
              }
              return nextIdx < displayedOptions.length ? nextIdx : prev;
            });
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setFocusedIndex((prev) => {
              let nextIdx = prev - 1;
              while (nextIdx >= 0 && displayedOptions[nextIdx].disabled) {
                nextIdx--;
              }
              return nextIdx >= 0 ? nextIdx : prev;
            });
          }
          break;
        case "Tab":
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [disabled, isOpen, focusedIndex, displayedOptions, handleSelect, preventBlurOnSelect]
  );

  const handleScroll = useCallback((e) => {
    if (!hasMore || isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      onLoadMore?.();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const containerClassName = getClassName(
    styles.container,
    "select-container",
    {
      [styles.fullWidth]: fullWidth,
      "select-full-width": fullWidth,
      [styles.containerFloatingLabel]: !!floatingLabel,
    },
    classes.root,
    externalClassName,
  );

  const hasValue = value !== undefined && value !== null && value !== "";
  const isFloating = isOpen || hasValue;

  const labelClassName = getClassName(
    floatingLabel ? styles.labelFloating : styles.label,
    floatingLabel ? "select-label-floating" : "select-label",
    {
      [styles.labelError]: !floatingLabel && error,
      "select-label-error": !floatingLabel && error,
      [styles.labelFloatingActive]: !!floatingLabel && isFloating,
      "select-label-floating-active": !!floatingLabel && isFloating,
      [styles.labelFloatingError]: !!floatingLabel && error,
      "select-label-floating-error": !!floatingLabel && error,
      [styles.labelFloatingDisabled]: !!floatingLabel && disabled,
      "select-label-floating-disabled": !!floatingLabel && disabled,
    },
    classes.label,
  );

  const triggerClassName = getClassName(
    styles.trigger,
    "select-trigger",
    sizeClass,
    {
      [styles.triggerSearchable]: isSearchable,
      "select-trigger-searchable": isSearchable,
      [styles.triggerOpen]: isOpen,
      "select-trigger-open": isOpen,
      [styles.triggerError]: error,
      "select-trigger-error": error,
    },
    classes.trigger,
  );

  const isErrorText = typeof error === "string" ? error : null;
  const bottomText = isErrorText || helperText;



  const selectedValues = useMemo(() => {
    return value !== undefined && value !== null && value !== "" ? [value] : [];
  }, [value]);

  if (isMultiMode) {
    return (
      <MultiSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        label={label}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        searchable={searchable}
        menuPortal={menuPortal}
        limitWidth={limitWidth}
        className={externalClassName}
        classes={classes}
        style={style}
        name={name}
        id={id}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        simulateNativeOnChange={simulateNativeOnChange}
        floatingLabel={floatingLabel}
        tooltip={tooltip}
        tooltipLight={tooltipLight}
        {...rest}
      />
    );
  }

  // Native customizable select rendering when supported & no advanced search/async features
  if (native && isBaseSelectSupported && !searchable && !asyncOptions && !onLoadMore) {
    return (
      <NativeSelect
        normalizedOptions={normalizedOptions}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        label={label}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        className={externalClassName}
        classes={classes}
        style={style}
        id={id}
        simulateNativeOnChange={simulateNativeOnChange}
        arrowIcon={arrowIcon}
        floatingLabel={floatingLabel}
        tooltip={tooltip}
        tooltipLight={tooltipLight}
        {...rest}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={maxMenuWidth != null ? { ...containerStyle, '--select-max-menu-width': typeof maxMenuWidth === 'number' ? `${maxMenuWidth}px` : maxMenuWidth } : containerStyle}
      id={id}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      {...rest}
    >
      {label && !floatingLabel && <label className={labelClassName}>{label}</label>}
      <div className={getClassName(styles.selectWrapper, "select-wrapper")}>
        {label && floatingLabel && <label className={labelClassName}>{label}</label>}
        {label && floatingLabel && <span className={styles.labelGhost} aria-hidden="true">{label}</span>}
        <SelectTrigger
          isSearchable={isSearchable}
          isOpen={isOpen}
          searchValue={searchValue}
          selectedOption={selectedOption}
          placeholder={(floatingLabel && !isFloating) ? "" : placeholder}
          disabled={disabled}
          triggerClassName={triggerClassName}
          triggerStyle={height ? { height: typeof height === "number" ? `${height}px` : height } : undefined}
          triggerRef={triggerRef}
          handleInputChange={handleInputChange}
          handleInputClick={handleInputClick}
          handleInputFocus={handleInputFocus}
          handleKeyDown={handleKeyDown}
          handleToggle={handleToggle}
          handleArrowClick={handleArrowClick}
          handleClearSearch={handleClearSearch}
          arrowIcon={arrowIcon}
          tooltip={tooltip}
          tooltipLight={tooltipLight}
        />

        {floatingLabel && (
          <fieldset className={getClassName(styles.notchedOutline, "select-notched-outline", { [styles.notchedOutlinefieldsetActive]: isFloating, })} aria-hidden="true">
            <legend className={getClassName(styles.notchedOutlineLegend, "select-notched-outline-legend", { [styles.notchedOutlineLegendActive]: isFloating })}>
              <span>{label}</span>
            </legend>
          </fieldset>
        )}

        <SelectMenu
          isOpen={isOpen}
          menuRef={menuRef}
          listRef={listRef}
          classes={classes}
          menuPortal={menuPortal}
          menuPos={menuPos}
          portalTarget={portalTarget}
          isLoading={isLoading}
          displayedOptions={displayedOptions}
          selectedValues={selectedValues}
          focusedIndex={focusedIndex}
          handleSelect={handleSelect}
          setFocusedIndex={setFocusedIndex}
          handleScroll={handleScroll}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          multiple={false}
          menuSearch={menuSearch}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          limitWidth={limitWidth}
          maxMenuWidth={maxMenuWidth}
          maxMenuHeight={maxMenuHeight}
          minMenuWidth={minMenuWidth}
        />
      </div>

      <input
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        required={rest.required}
        value={selectedOption ? String(selectedOption.value) : ""}
        onChange={() => {}}
        style={{
          opacity: 0,
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "1px",
          pointerEvents: "none",
          zIndex: -1,
        }}
      />

      {bottomText && (
        <div
          className={getClassName(
            styles.helperText,
            "select-helper-text",
            {
              [styles.errorText]: isErrorText,
              "select-error-text": isErrorText,
            }
          )}
        >
          {bottomText}
        </div>
      )}
    </div>
  );
};

export default Select;
